import { useAsyncState } from '@vueuse/core';
import type { Collection } from '@/login/composables/useResgate';
import { useResgate } from '@/login/composables/useResgate';
import type { WorkTag } from '@sysarb/sysarb-types-v2/services/v1/pea';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { storeToRefs } from 'pinia';
import { onUnmounted } from 'vue';

type UseWorkTagsReturn = {
    workTagsState: ReturnType<typeof useAsyncState<Collection<WorkTag> | null>>;
    getWorkTags: () => Promise<Collection<WorkTag> | null>;
    transformWorkTagsToMap: (workTags: WorkTag[]) => Map<WorkTag['id'], WorkTag>;
};

/**
 * Transforms the work tags to a map of id to work tag.
 * @param workTags the work tags to transform
 * @returns a map of id to work tag
 */
export function transformWorkTagsToMap(workTags: WorkTag[]): Map<WorkTag['id'], WorkTag> {
    return new Map(workTags.map(tag => [tag.id, tag]));
}

/**
 * A composable that provides the work tags for the current instance.
 * @returns states and functions to get the work tags
 */
export const useWorkTags = (): UseWorkTagsReturn => {
    const _resgate = useResgate(crypto.randomUUID());
    const { instanceTopic } = storeToRefs(useLoginStore());

    const workTagsState = useAsyncState<Collection<WorkTag> | null>(getWorkTags, null, { immediate: false });

    /**
     * Get the work tags for the current instance.
     * @returns the work tags for the current instance
     */
    async function getWorkTags(): Promise<Collection<WorkTag> | null> {
        return (await _resgate.collection<WorkTag>(`v1.${instanceTopic.value}.tags`)).data;
    }

    onUnmounted(() => {
        _resgate.unsubscribe();
    });

    return {
        workTagsState,
        getWorkTags,
        transformWorkTagsToMap
    };
};
